import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import React from 'react';
import { AuthData, withAuthContext } from '../context/auth';
import { CurrencyCode } from '../types/currencyCode';
import { PolicyQuoteInvoiceResponse } from '../types/policy';
import { getComparator, HeadCell, Order, stableSort } from '../util/tableUtil';
import { formatCurrency } from '../util/util';
import { StyledEmptyMessage } from './common/index';
import SortableTable from './SortableTable';

interface Props {
	invoices: PolicyQuoteInvoiceResponse[];
	authData: AuthData;
	currency: CurrencyCode;
}

interface Data {
	issueDate: number;
	dueDate: number;
	premium: string;
	fees: string;
	taxes: string;
	holdbacks: string;
	totalDue: string;
}

function createData(
	issueDate: number,
	dueDate: number,
	premium: string,
	fees: string,
	taxes: string,
	holdbacks: string,
	totalDue: string,
): Data {
	return { issueDate, dueDate, premium, fees, taxes, holdbacks, totalDue };
}

const InvoicesTable = (props: Props) => {
	const { invoices, authData, currency } = props;
	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] = React.useState<string>('issueDate');

	const emptyTable = !invoices.length ? (
		<StyledEmptyMessage>No Future Invoices</StyledEmptyMessage>
	) : null;

	const headCells: HeadCell[] = [
		{ id: 'issueDate', rightAlign: false, label: 'Issue Date' },
		{ id: 'dueDate', rightAlign: false, label: 'Due Date' },
		{ id: 'premium', rightAlign: true, label: 'Premium' },
		{ id: 'fees', rightAlign: true, label: 'Fees' },
		{ id: 'taxes', rightAlign: true, label: 'Taxes' },
		{ id: 'holdbacks', rightAlign: true, label: 'Holdbacks' },
		{ id: 'totalDue', rightAlign: true, label: 'Total Due' },
	];

	if (!invoices.length) {
		return (
			<SortableTable
				rows={[]}
				headCells={headCells}
				additionalMsg={emptyTable}
				order={order}
				setOrder={setOrder}
				orderBy={orderBy}
				setOrderBy={setOrderBy}
			/>
		);
	}

	const { financialTransactions } = invoices[0];

	// if no financial transactions, display empty table
	if (!financialTransactions || !financialTransactions.length) {
		return (
			<SortableTable
				rows={[]}
				headCells={headCells}
				additionalMsg={emptyTable}
				order={order}
				setOrder={setOrder}
				orderBy={orderBy}
				setOrderBy={setOrderBy}
			/>
		);
	}

	const calculateTotals = (transactions: any[]) => {
		let premium = 0;
		let taxes = 0;
		let fees = 0;
		let holdbacks = 0;
		let total = 0;
		transactions.forEach((f) => {
			switch (f.type) {
				case 'premium':
					premium += parseFloat(f.amount);
					total += parseFloat(f.amount);
					break;
				case 'tax':
					taxes += parseFloat(f.amount);
					total += parseFloat(f.amount);
					break;
				case 'fee':
					fees += parseFloat(f.amount);
					total += parseFloat(f.amount);
					break;
				case 'holdback':
					holdbacks += parseFloat(f.amount);
					total += parseFloat(f.amount);
					break;
				default:
					// additionally total up any other non-commission transaction types not already called out
					// allows for future types to be added without creating invalid total in the UI
					if (f.type !== 'commission') {
						total += parseFloat(f.amount);
					}
			}
		});
		return [premium, taxes, fees, holdbacks, total];
	};

	const rows: any = invoices.map((invoice, index: number) => {
		const vals = calculateTotals(invoice.financialTransactions);
		return createData(
			invoice.issueTimestamp,
			invoice.dueTimestamp ? invoice.dueTimestamp : 0,
			formatCurrency(vals[0], currency),
			formatCurrency(vals[2], currency),
			formatCurrency(vals[1], currency),
			formatCurrency(vals[3], currency),
			formatCurrency(vals[4], currency),
		);
	});

	const rowElements = stableSort(rows, getComparator(order, orderBy)).map(
		(row: any, index: number) => {
			return (
				<TableRow hover tabIndex={-1} key={`future-invoice-${index}`}>
					<TableCell scope='row'>
						{authData.getFormattedDate(row.issueDate)}
					</TableCell>
					<TableCell scope='row'>
						{authData.getFormattedDate(row.dueDate)}
					</TableCell>
					<TableCell align='right'>{row.premium}</TableCell>
					<TableCell align='right'>{row.fees}</TableCell>
					<TableCell align='right'>{row.taxes}</TableCell>
					<TableCell align='right'>{row.holdbacks}</TableCell>
					<TableCell align='right'>{row.totalDue}</TableCell>
				</TableRow>
			);
		},
	);

	return (
		<SortableTable
			rows={rowElements}
			headCells={headCells}
			additionalMsg={emptyTable}
			order={order}
			setOrder={setOrder}
			orderBy={orderBy}
			setOrderBy={setOrderBy}
		/>
	);
};

export default withAuthContext<Props>(InvoicesTable);
